// Pure validation for the P1-18 Phase 2 catalog edits.
//
// Two editable fields, both nullable in the DB:
//   items.tag_name_override                   text, 1..100 chars
//   item_firearm_attributes.features          text[], <=8, each 1..30 chars
//
// Rules match the Phase 1 DB CHECK constraints exactly so a value that
// passes here is guaranteed to pass the server-side insert. If the two
// drift, the integration tests will catch it (the DB raises
// features_valid or the tag_name_override length CHECK).
//
// Business rules live once (rule 2): both the device (instant feedback
// as the user types) and the server (authoritative) import from here.

export const TAG_OVERRIDE_MAX = 100;
export const FEATURES_MAX_COUNT = 8;
export const FEATURE_MAX_LEN = 30;

export type ValidationError =
  | { ok: false; reason: "empty_after_trim"; field?: string }
  | { ok: false; reason: "too_long"; max: number; got: number }
  | { ok: false; reason: "too_many"; max: number; got: number }
  | { ok: false; reason: "not_array" }
  | { ok: false; reason: "not_string"; index: number }
  | { ok: false; reason: "not_integer"; field: string }
  | { ok: false; reason: "not_positive"; field: string }
  | { ok: false; reason: "not_boolean"; field: string }
  | { ok: false; reason: "bad_enum"; field: string; allowed: readonly string[] }
  | { ok: false; reason: "missing_field"; field: string };

export type ValidationOk<T> = { ok: true; value: T };
export type ValidationResult<T> = ValidationOk<T> | ValidationError;

/**
 * Validates a candidate tag_name_override.
 *
 * - `null` means "no override" — valid, returns `{ ok: true, value: null }`.
 * - A string is trimmed; empty-after-trim rejects.
 * - Length cap matches the DB CHECK (char_length 1..100).
 *
 * The server stores exactly what this returns; the UI calls this on
 * keystroke to drive the save-button enabled state.
 */
export function validateTagOverride(input: string | null): ValidationResult<string | null> {
  if (input === null) return { ok: true, value: null };
  const trimmed = input.trim();
  if (trimmed.length === 0) return { ok: false, reason: "empty_after_trim" };
  if (trimmed.length > TAG_OVERRIDE_MAX) {
    return { ok: false, reason: "too_long", max: TAG_OVERRIDE_MAX, got: trimmed.length };
  }
  return { ok: true, value: trimmed };
}

/**
 * Validates a candidate features[] array.
 *
 * - `null` and the empty array both mean "clear features". The server
 *   stores `null` either way (NULL = unset per Phase 1 brief).
 * - Each entry is trimmed; empty-after-trim rejects the whole array
 *   so the UI can point at the offending chip.
 * - Max 8 entries; each 1..30 chars; matches the DB
 *   core.features_valid() function from Phase 1.
 *
 * The returned value is either `null` (clear) or a cleaned array of
 * trimmed strings in input order.
 */
export function validateFeatures(input: unknown): ValidationResult<string[] | null> {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (!Array.isArray(input)) return { ok: false, reason: "not_array" };
  if (input.length === 0) return { ok: true, value: null };
  if (input.length > FEATURES_MAX_COUNT) {
    return { ok: false, reason: "too_many", max: FEATURES_MAX_COUNT, got: input.length };
  }
  const cleaned: string[] = [];
  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    if (typeof raw !== "string") return { ok: false, reason: "not_string", index: i };
    const trimmed = raw.trim();
    if (trimmed.length === 0) return { ok: false, reason: "empty_after_trim" };
    if (trimmed.length > FEATURE_MAX_LEN) {
      return { ok: false, reason: "too_long", max: FEATURE_MAX_LEN, got: trimmed.length };
    }
    cleaned.push(trimmed);
  }
  return { ok: true, value: cleaned };
}

// ---------------------------------------------------------------------------
// P1-18 PR B — per-kind attribute validators (ammo / optics / holster).
//
// Each validator mirrors the DB CHECK constraints from PR A exactly: an
// input that passes here is guaranteed to pass the server-side UPDATE. If
// the two drift, the integration tests catch it (the DB raises a 23514).
//
// Business rules live once (rule 2): the sheet UI calls these on every
// keystroke; the API PATCH handler calls them again before writing.
// ---------------------------------------------------------------------------

export const AMMO_BULLET_TYPES = ["fmj", "jhp", "sp", "hp", "match", "frangible", "other"] as const;
export type AmmoBulletType = (typeof AMMO_BULLET_TYPES)[number];

export const HOLSTER_MOUNT_TYPES = [
  "iwb",
  "owb",
  "pocket",
  "shoulder",
  "chest",
  "ankle",
  "drop_leg",
] as const;
export type HolsterMountType = (typeof HOLSTER_MOUNT_TYPES)[number];

export const HOLSTER_DRAW_SIDES = ["left", "right", "ambidextrous"] as const;
export type HolsterDrawSide = (typeof HOLSTER_DRAW_SIDES)[number];

export const HOLSTER_MATERIALS = ["kydex", "leather", "nylon", "hybrid", "other"] as const;
export type HolsterMaterial = (typeof HOLSTER_MATERIALS)[number];

export type AmmoAttributesInput = {
  manufacturer: string;
  caliberGauge: string;
  grain: number | null;
  bulletType: AmmoBulletType | null;
  isHollowPoint: boolean;
  roundsPerBox: number;
  muzzleVelocityFps: number | null;
};

export type OpticsAttributesInput = {
  manufacturer: string;
  model: string;
  magnification: string;
  reticle: string | null;
  mount: string | null;
  isIlluminated: boolean;
  batteryType: string | null;
};

export type HolsterAttributesInput = {
  manufacturer: string;
  fitModel: string;
  mountType: HolsterMountType;
  drawSide: HolsterDrawSide;
  retentionLevel: string | null;
  material: HolsterMaterial | null;
};

type Dict = Record<string, unknown>;

function isDict(x: unknown): x is Dict {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function requireTrimmed(obj: Dict, key: string): ValidationResult<string> {
  const v = obj[key];
  if (typeof v !== "string") return { ok: false, reason: "missing_field", field: key };
  const trimmed = v.trim();
  if (trimmed.length === 0) return { ok: false, reason: "empty_after_trim", field: key };
  return { ok: true, value: trimmed };
}

function optionalTrimmed(obj: Dict, key: string): ValidationResult<string | null> {
  const v = obj[key];
  if (v === null || v === undefined) return { ok: true, value: null };
  if (typeof v !== "string") return { ok: false, reason: "missing_field", field: key };
  const trimmed = v.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  return { ok: true, value: trimmed };
}

function requirePositiveInt(obj: Dict, key: string): ValidationResult<number> {
  const v = obj[key];
  if (typeof v !== "number" || !Number.isInteger(v)) {
    return { ok: false, reason: "not_integer", field: key };
  }
  if (v <= 0) return { ok: false, reason: "not_positive", field: key };
  return { ok: true, value: v };
}

function optionalPositiveInt(obj: Dict, key: string): ValidationResult<number | null> {
  const v = obj[key];
  if (v === null || v === undefined) return { ok: true, value: null };
  if (typeof v !== "number" || !Number.isInteger(v)) {
    return { ok: false, reason: "not_integer", field: key };
  }
  if (v <= 0) return { ok: false, reason: "not_positive", field: key };
  return { ok: true, value: v };
}

function requireBoolean(obj: Dict, key: string): ValidationResult<boolean> {
  const v = obj[key];
  if (typeof v !== "boolean") return { ok: false, reason: "not_boolean", field: key };
  return { ok: true, value: v };
}

function requireEnum<T extends string>(
  obj: Dict,
  key: string,
  allowed: readonly T[],
): ValidationResult<T> {
  const v = obj[key];
  if (typeof v !== "string" || !(allowed as readonly string[]).includes(v)) {
    return { ok: false, reason: "bad_enum", field: key, allowed };
  }
  return { ok: true, value: v as T };
}

function optionalEnum<T extends string>(
  obj: Dict,
  key: string,
  allowed: readonly T[],
): ValidationResult<T | null> {
  const v = obj[key];
  if (v === null || v === undefined) return { ok: true, value: null };
  if (typeof v !== "string" || !(allowed as readonly string[]).includes(v)) {
    return { ok: false, reason: "bad_enum", field: key, allowed };
  }
  return { ok: true, value: v as T };
}

export function validateAmmoAttributes(input: unknown): ValidationResult<AmmoAttributesInput> {
  if (!isDict(input)) return { ok: false, reason: "missing_field", field: "attributes" };
  const manufacturer = requireTrimmed(input, "manufacturer");
  if (!manufacturer.ok) return manufacturer;
  const caliberGauge = requireTrimmed(input, "caliberGauge");
  if (!caliberGauge.ok) return caliberGauge;
  const grain = optionalPositiveInt(input, "grain");
  if (!grain.ok) return grain;
  const bulletType = optionalEnum(input, "bulletType", AMMO_BULLET_TYPES);
  if (!bulletType.ok) return bulletType;
  const isHollowPoint = requireBoolean(input, "isHollowPoint");
  if (!isHollowPoint.ok) return isHollowPoint;
  const roundsPerBox = requirePositiveInt(input, "roundsPerBox");
  if (!roundsPerBox.ok) return roundsPerBox;
  const muzzleVelocityFps = optionalPositiveInt(input, "muzzleVelocityFps");
  if (!muzzleVelocityFps.ok) return muzzleVelocityFps;
  return {
    ok: true,
    value: {
      manufacturer: manufacturer.value,
      caliberGauge: caliberGauge.value,
      grain: grain.value,
      bulletType: bulletType.value,
      isHollowPoint: isHollowPoint.value,
      roundsPerBox: roundsPerBox.value,
      muzzleVelocityFps: muzzleVelocityFps.value,
    },
  };
}

export function validateOpticsAttributes(input: unknown): ValidationResult<OpticsAttributesInput> {
  if (!isDict(input)) return { ok: false, reason: "missing_field", field: "attributes" };
  const manufacturer = requireTrimmed(input, "manufacturer");
  if (!manufacturer.ok) return manufacturer;
  const model = requireTrimmed(input, "model");
  if (!model.ok) return model;
  const magnification = requireTrimmed(input, "magnification");
  if (!magnification.ok) return magnification;
  const reticle = optionalTrimmed(input, "reticle");
  if (!reticle.ok) return reticle;
  const mount = optionalTrimmed(input, "mount");
  if (!mount.ok) return mount;
  const isIlluminated = requireBoolean(input, "isIlluminated");
  if (!isIlluminated.ok) return isIlluminated;
  const batteryType = optionalTrimmed(input, "batteryType");
  if (!batteryType.ok) return batteryType;
  return {
    ok: true,
    value: {
      manufacturer: manufacturer.value,
      model: model.value,
      magnification: magnification.value,
      reticle: reticle.value,
      mount: mount.value,
      isIlluminated: isIlluminated.value,
      batteryType: batteryType.value,
    },
  };
}

export function validateHolsterAttributes(
  input: unknown,
): ValidationResult<HolsterAttributesInput> {
  if (!isDict(input)) return { ok: false, reason: "missing_field", field: "attributes" };
  const manufacturer = requireTrimmed(input, "manufacturer");
  if (!manufacturer.ok) return manufacturer;
  const fitModel = requireTrimmed(input, "fitModel");
  if (!fitModel.ok) return fitModel;
  const mountType = requireEnum(input, "mountType", HOLSTER_MOUNT_TYPES);
  if (!mountType.ok) return mountType;
  const drawSide = requireEnum(input, "drawSide", HOLSTER_DRAW_SIDES);
  if (!drawSide.ok) return drawSide;
  const retentionLevel = optionalTrimmed(input, "retentionLevel");
  if (!retentionLevel.ok) return retentionLevel;
  const material = optionalEnum(input, "material", HOLSTER_MATERIALS);
  if (!material.ok) return material;
  return {
    ok: true,
    value: {
      manufacturer: manufacturer.value,
      fitModel: fitModel.value,
      mountType: mountType.value,
      drawSide: drawSide.value,
      retentionLevel: retentionLevel.value,
      material: material.value,
    },
  };
}
